import { CheckCircleTwoTone, PrinterOutlined } from '@ant-design/icons';
import { Col, DatePicker, Input, Layout, message, Row, Select, Tooltip } from 'antd';
import { useContext, useEffect, useState } from 'react';
import DataTable from 'react-data-table-component';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import order from 'services/order';
import ShipmentService from 'services/shipment';

import { ProfileContext } from 'contexts/profile';

import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import queryString from 'query-string';
import addDay from 'utils/addDay';

dayjs.extend(customParseFormat);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

import { get } from 'lodash';

import PdButton from 'components/pd-button';

const { Content } = Layout;
const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

const dateFormat = 'YYYY/MM/DD';

export function Shipments() {
  const location = useLocation();
  const [loadStatus, setLoadStatus] = useState(true);
  const [couriers, setCouriers] = useState([]);
  const [selectedCourier, setSelectedCourier] = useState('');
  const [orderList, setOrderList] = useState([]);
  const [page, setPage] = useState(1);
  const [nextPage, setNextPage] = useState(1);
  const [sortBy, setSortBy] = useState('ordered_at desc');
  const navigate = useNavigate();
  const parsed = queryString.parse(location.search);
  const [searchValue, setSearchValue] = useState('');
  const [startDatePick, setStartDate] = useState('');
  const [endDatePick, setEndDate] = useState('');

  const { fetchProfile } = useContext(ProfileContext);

  const customElement = (row) => (
    <div
      className="button-table"
      style={{
        display: 'flex',
        gap: 8,
        alignItems: 'center',
        justifyContent: 'flex-end',
        width: '100%',
        flexWrap: 'nowrap',
      }}>
      {row.status === 'processed' && (
        <Tooltip title="Print shipping label">
          <PrinterOutlined
            style={{ fontSize: '18px', cursor: 'pointer', padding: '6px' }}
            onClick={() => printLabel(row.id)}
          />
        </Tooltip>
      )}

      <Link to={`/shipments/detail?id=${row.id}`}>
        <PdButton type="primary" style={{ height: 32, minWidth: 80 }}>
          Detail
        </PdButton>
      </Link>
    </div>
  );

  customElement.displayName = 'customButton';

  const checkIcon = (row) => {
    return (
      row.shipping_label_url && <CheckCircleTwoTone twoToneColor="#52c41a" />
    );
  };

  const printLabel = async (id) => {
    const { data } = await order.getOrderShippingLabel(id);
    message.success('Shipping label printed successfully');
    // fetchOrderList();
    const shipping_label = get(data, 'data.shipping_label_url', '');
    window.open(shipping_label);
  };

  checkIcon.displayName = 'customIcon';

  const [columns] = useState([
    {
      name: 'Order ID.',
      selector: row => row.formatted_id,
      width: '16%',
    },
    {
      name: 'Package ID.',
      selector: row => row.format_package_id,
      width: '16%',
    },
    {
      name: 'Date',
      selector: row => row.ordered_at,
      format: (row) => {
        return dayjs(row.ordered_at).format('DD/MM/YYYY');
      },
      width: '10%',
      center: true,
    },
    {
      name: 'Customer',
      selector: row => row.user.full_name, // Keep this as a fallback selector for sorting if needed by react-data-table-component
      width: '10%',
      format: (row) => {
        const fullName = get(row, 'user.full_name');
        if (fullName) {
          return fullName;
        }
        return get(row, 'user.recipient_name', ''); // Return recipient_name or empty string if null
      },
    },
    {
      name: 'Courier',
      selector: row => row.courier.title, // Still good for default sorting if courier.title exists
      width: '8%',
      format: (row) => {
        const courier = get(row, 'courier', {});
        // Check if the courier object is empty
        if (Object.keys(courier).length === 0) {
          const pickupPoint = get(row, 'pickup_point');
          // If pickup_point exists and is not an empty array/object
          if (pickupPoint && Object.keys(pickupPoint).length > 0) {
            return 'Pickup';
          }
        }
        // If courier object is not empty, or if pickup_point is also empty, return courier.title
        return get(row, 'courier.title', '');
      },
    },
    {
      name: 'Printed',
      selector: row => row.shipping_label_url,
      center: true,
      format: checkIcon,
      width: '8%',
    },
    {
      name: 'Platforms',
      // Corrected selector to 'user.email' for better consistency with 'get' usage
      selector: row => row.user.email,
      width: '10%',
      center: true,
      format: (row) => {
        // Use lodash get to safely access nested properties
        const email = get(row, 'user.email', '');
        const whitelisted_emails = [
          'delima@email.aditya.co.id',
          'adriansyah@email.aditya.co.id',
          'nopi.andarista@email.aditya.co.id',
          'damar@email.aditya.co.id',
          'reza.aulia@email.aditya.co.id',
          'rayessa.laffayza@email.aditya.co.id',
          'idd@homestory.co.id',
          'admin@homestory.co.id',
          'bali@homestory.co.id',
          'dev@homestory.co.id',
          'appsdev.test@aditya.co.id',
        ];
        // Check if the email is an exact match for any of the whitelisted emails
        const isWhitelisted = whitelisted_emails.includes(email);
        if (isWhitelisted) {
          return 'Offline';
        }
        return 'Online';
      },
    },
    {
      name: 'Status',
      selector: row => row.status,
      format: (row) => {
        let str = row.status.split('_');
        str = str.map((item) => {
          return item.charAt(0).toUpperCase() + item.slice(1);
        });
        return str.join(' ');
      },
      width: '10%',
      center: true,
    },
    {
      name: '',
      cell: customElement,
      center: true,
      width: '12%',
      allowOverflow: true,
      compact: true,
    },
  ]);

  async function fetchOrderList(
    page = 1,
    total = 10,
    query,
    sort,
    courier,
    startDate,
    endDate,
    filter
  ) {
    setLoadStatus(true);
    let newEndDate = endDate;
    if (newEndDate) {
      newEndDate = addDay(endDate, 1);
    }
    try {
      const payload = {
        courier_id: courier,
        cursor: page,
        end_date: newEndDate,
        limit: total,
        q: query,
        sort_by: sort,
        start_date: startDate,
        status: filter,
      };
      const { data } = await ShipmentService.getOrderList(payload);
      const orderData = data.data;
      const metadata = data.metadata;
      setNextPage(metadata.next_cursor);
      setLoadStatus(false);
      setOrderList(orderData);
    } finally {
      setLoadStatus(false);
    }
  }

  async function getCourierList() {
    try {
      const { data } = await ShipmentService.getCourierList();
      const CourierList = data.data;
      setCouriers(CourierList);
    } catch (err) {
      const errMessage = get(
        get(get(err, 'response', {}), 'data', {}),
        'message',
        ''
      );
      if (errMessage) {
        message.error(errMessage);
      } else {
        message.error('Cannot connect to server, please check connection');
      }
    }
  }

  function changePage(page) {
    if (page < 1) page = 1;
    parsed['page'] = page;
    const stringified = queryString.stringify(parsed);
    navigate({
      pathname: '/shipments',
      search: stringified,
    });
  }

  function onChangeDate(dates, datesString) {
    const startDate = datesString[0];
    if (startDate === '') {
      delete parsed['startDate'];
    } else {
      parsed['startDate'] = startDate;
    }

    const endDate = datesString[1];
    if (endDate === '') {
      delete parsed['endDate'];
    } else {
      parsed['endDate'] = endDate;
    }

    const stringified = queryString.stringify(parsed);
    navigate({
      pathname: '/shipments',
      search: stringified,
    });
  }

  function filterOrder(param, val) {
    if (val === '' || val === 0) {
      delete parsed[param];
    } else {
      parsed[param] = val;
    }
    const stringified = queryString.stringify(parsed);
    navigate({
      pathname: '/shipments',
      search: stringified,
    });
  }

  function resetFilter() {
    delete parsed['page'];
    delete parsed['q'];
    delete parsed['status'];
    delete parsed['sortBy'];
    delete parsed['courier'];
    delete parsed['startDate'];
    delete parsed['endDate'];
    delete parsed['limit'];

    const stringified = queryString.stringify(parsed);
    navigate({
      pathname: '/shipments',
      search: stringified,
    });
  }

  useEffect(() => {
    getCourierList();
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  /* eslint-disable */
  useEffect(() => {
    if (parsed['q']) {
      setSearchValue(parsed['q']);
    } else {
      setSearchValue('');
    }

    if (parsed['page']) {
      if (parsed['page'] < 1) {
        parsed['page'] = 1;
        const stringified = queryString.stringify(parsed);
        navigate({
          pathname: '/shipments',
          search: stringified,
        });
      } else {
        setPage(parsed['page']);
      }
    } else {
      setPage('1');
    }

    if (parsed['sortBy']) {
      setSortBy(parsed['sortBy']);
    } else {
      setSortBy('ordered_at desc');
    }

    if (parsed['courier']) {
      setSelectedCourier(parsed['courier']);
    } else {
      setSelectedCourier(0);
    }

    if (parsed['startDate']) {
      setStartDate(parsed['startDate']);
    } else {
      setStartDate('');
    }

    if (parsed['endDate']) {
      setEndDate(parsed['endDate']);
    } else {
      setEndDate('');
    }

    fetchOrderList(
      parsed['page'],
      10,
      parsed['q'],
      parsed['sortBy'],
      parsed['courier'],
      parsed['startDate'],
      parsed['endDate']
    );
  }, [
    parsed['page'],
    parsed['q'],
    parsed['sortBy'],
    parsed['courier'],
    parsed['startDate'],
    parsed['endDate'],
  ]);
  /* eslint-enable */

  return (
    <Layout className="pd-cms-shipments main-container">
      <div className="content">
        <div className="pageTitle">Shipments</div>
        <Content className="pageContent">
          <Row gutter={[18, 18]}>
            <Col span={14}>
              <Search
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                enterButton
                className="search-product"
                placeholder="Search Order List"
                onSearch={(val) => filterOrder('q', val)}
              />
            </Col>
            <Col span={24}>
              <Row gutter={18}>
                <Col span={7}>
                  <RangePicker
                    className="w-full"
                    format={dateFormat}
                    onChange={onChangeDate}
                    value={[
                      startDatePick && dayjs(startDatePick, dateFormat),
                      endDatePick && dayjs(endDatePick, dateFormat),
                    ]}
                  />
                </Col>
                <Col span={4}>
                  <Select
                    style={{ width: '100%' }}
                    value={Number(selectedCourier)}
                    onChange={(val) => filterOrder('courier', val)}>
                    <Option value={0}>All Couriers</Option>
                    {couriers.map((courier, i) => (
                      <Option key={i} value={courier.id}>
                        {courier.title}
                      </Option>
                    ))}
                  </Select>
                </Col>
                <Col span={3}>
                  <Select
                    value={sortBy}
                    style={{ width: '100%' }}
                    onChange={(val) => filterOrder('sortBy', val)}>
                    <Option value="ordered_at desc">Latest</Option>
                    <Option value="ordered_at asc">Oldest</Option>
                    <Option value="status">Status</Option>
                  </Select>
                </Col>
                <Col span={4}>
                  <span className="reset-filter" onClick={resetFilter}>
                    Reset Filter
                  </span>
                </Col>
              </Row>
            </Col>

            {!loadStatus && (
              <>
                <Col xs={24}>
                  <DataTable
                    columns={columns}
                    data={orderList}
                    progressPending={loadStatus}
                    noHeader
                    highlightOnHover
                    striped
                  />
                </Col>

                <Col span={8} offset={8}>
                  <div className="center-button">
                    {page !== '1' && (
                      <PdButton
                        type="primary"
                        outline
                        style={{ height: 32 }}
                        onClick={() => changePage(Number(page) - 1)}>
                        Previous
                      </PdButton>
                    )}
                    {nextPage !== -1 && (
                      <PdButton
                        type="primary"
                        outline
                        style={{ height: 32 }}
                        onClick={() => changePage(Number(page) + 1)}>
                        Next
                      </PdButton>
                    )}
                  </div>
                </Col>
              </>
            )}
          </Row>
        </Content>
      </div>
    </Layout>
  );
}

export default { Shipments };
